import { useEffect, useMemo, useRef, useState } from 'react'
import { Camera } from 'lucide-react'
import { toast } from 'sonner'
import { BarcodeScanner } from '@/components/BarcodeScanner'
import { BookReservationItem } from '@/components/BookReservationItem'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuth } from '@/hooks/useAuth'
import { useBooks } from '@/hooks/useBooks'
import { useFriends } from '@/hooks/useFriends'
import { useLoans } from '@/hooks/useLoans'
import { lookupBookByIsbn, searchBooks } from '@/lib/openLibrary'
import type { LookupBookResult } from '@/lib/openLibrary'
import type { Friend, LoanType } from '@/types/booky'

export const MoveBook = () => {
  const { user } = useAuth()
  const { friends, addFriend } = useFriends(user?.id)
  const { upsertBook } = useBooks()
  const { activeLoans, createLoan, returnLoan } = useLoans(user?.id)

  const [submitting, setSubmitting] = useState(false)
  const [loanType, setLoanType] = useState<LoanType>('on-loan')
  const [scannerOpen, setScannerOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<LookupBookResult[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [lookupResult, setLookupResult] = useState<LookupBookResult | null>(null)
  const [friendId, setFriendId] = useState<string>('')
  const [quickFriendName, setQuickFriendName] = useState('')
  const [addingFriend, setAddingFriend] = useState(false)
  const [returning, setReturning] = useState(false)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  const hasFriends = friends.length > 0
  const selectedFriend = useMemo(() => friends.find((friend) => String(friend.id) === friendId), [friends, friendId])

  const activeLoan = useMemo(() => {
    if (!lookupResult) {
      return null
    }

    return activeLoans.find((loan) => loan.book.isbn13 === lookupResult.isbn13) ?? null
  }, [lookupResult, activeLoans])

  useEffect(() => {
    const query = searchQuery.trim()

    if (query.length < 2) {
      setSearchResults([])
      setShowSuggestions(false)
      return
    }

    const timer = setTimeout(() => {
      setSearchLoading(true)
      searchBooks(query)
        .then((results) => {
          setSearchResults(results)
          setShowSuggestions(results.length > 0)
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false))
    }, 350)

    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [])

  const resetForm = () => {
    setSearchQuery('')
    setSearchResults([])
    setShowSuggestions(false)
    setLookupResult(null)
    setFriendId('')
    setQuickFriendName('')
    setLoanType('on-loan')
    setScannerOpen(false)
    setReturning(false)
  }

  const clearBook = () => {
    setLookupResult(null)
    setSearchQuery('')
    setSearchResults([])
    setShowSuggestions(false)
    setScannerOpen(false)
  }

  const handleSelectSuggestion = (result: LookupBookResult) => {
    setLookupResult(result)
    setSearchQuery('')
    setSearchResults([])
    setShowSuggestions(false)
    setScannerOpen(false)
  }

  const runIsbnLookup = async (barcode: string) => {
    const value = barcode.trim()

    if (!value) {
      return
    }

    try {
      const result = await lookupBookByIsbn(value)
      setLookupResult(result)
      setScannerOpen(false)
      setSearchResults([])
      setShowSuggestions(false)
      toast.success('Book found.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to lookup book'
      toast.error(message)
    }
  }

  const handleScannerDetected = (value: string) => {
    void runIsbnLookup(value)
  }

  const handleBorrow = async (lookup: LookupBookResult, friendId: number, loanType: LoanType) => {
    if (!user) {
      return
    }

    setSubmitting(true)

    const upsertResult = await upsertBook({
      userId: user.id,
      isbn13: lookup.isbn13,
      title: lookup.title,
      authors: lookup.authors,
      coverUrl: lookup.coverUrl,
      rawPayload: lookup.rawPayload,
    })

    if (upsertResult.error || !upsertResult.data) {
      toast.error(upsertResult.error?.message ?? 'Failed to save book')
      setSubmitting(false)
      return
    }

    const { error } = await createLoan(upsertResult.data.id, friendId, loanType)

    if (error) {
      toast.error(error.message)
    } else {
      toast.success(
        loanType === 'on-loan'
          ? `${lookup.title} marked as on loan`
          : `${lookup.title} marked as borrowed`
      )
    }

    setSubmitting(false)
  }

  const handleAddFriend = async (name: string): Promise<{ friend: Friend | null; error: Error | null }> => {
    if (!user) {
      return { friend: null, error: new Error('User not signed in') }
    }

    const { data, error } = await addFriend(name)

    if (error) {
      return { friend: null, error }
    }

    if (!data) {
      return { friend: null, error: new Error('Failed to add friend') }
    }

    return { friend: data, error: null }
  }

  const handleReturn = async (loanId: number): Promise<{ error: Error | null }> => {
    const { error } = await returnLoan(loanId)
    return { error }
  }

  const handleReturnBook = async () => {
    if (!activeLoan) {
      return
    }

    setReturning(true)
    const { error } = await handleReturn(activeLoan.id)

    if (error) {
      toast.error(error.message)
      setReturning(false)
      return
    }

    toast.success(`${activeLoan.book.title} marked as returned`)
    resetForm()
  }

  const handleConfirmBooking = async () => {
    if (!lookupResult || !selectedFriend) {
      toast.error('Select a book and a friend first.')
      return
    }

    await handleBorrow(lookupResult, selectedFriend.id, loanType)
    resetForm()
  }

  const handleQuickAddFriend = async () => {
    const name = quickFriendName.trim()

    if (!name) {
      toast.error('Enter a friend name first.')
      return
    }

    setAddingFriend(true)
    const { friend, error } = await handleAddFriend(name)

    if (error || !friend) {
      toast.error(error?.message ?? 'Failed to add friend')
      setAddingFriend(false)
      return
    }

    setFriendId(String(friend.id))
    setQuickFriendName('')
    toast.success('Friend added')
    setAddingFriend(false)
  }

  const returnPrompt = activeLoan
    ? activeLoan.loan_type === 'on-loan'
      ? `This book is currently on loan to ${activeLoan.friend.name}. Would you like to mark it as returned?`
      : `You are borrowing this book from ${activeLoan.friend.name}. Would you like to mark it as returned?`
    : null

  const bookStatus = activeLoan?.loan_type
  const bookStatusText = activeLoan
    ? activeLoan.loan_type === 'on-loan'
      ? `Loaned to ${activeLoan.friend.name}`
      : `Borrowed from ${activeLoan.friend.name}`
    : 'No active loan status'

  return (
    <main className='mx-auto w-full max-w-5xl p-3 md:p-6'>
      <Card>
        <CardHeader>
          <CardTitle>Move book</CardTitle>
          <CardDescription>Search by title/author or scan an ISBN barcode.</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <form onSubmit={(event) => event.preventDefault()}>
            <div ref={searchContainerRef} className='relative'>
              <div className='flex items-center gap-2'>
                <Input
                  id='book-search'
                  type='text'
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value)
                    setLookupResult(null)
                    setScannerOpen(false)
                  }}
                  onFocus={() => searchResults.length > 0 && setShowSuggestions(true)}
                  placeholder='Search by title/author/ISBN'
                  autoComplete='off'
                  autoCorrect='off'
                  autoCapitalize='none'
                  spellCheck={false}
                  inputMode='search'
                  data-1p-ignore='true'
                  data-lpignore='true'
                />
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setScannerOpen((prev) => {
                      if (!prev) {
                        setSearchQuery('')
                        setSearchResults([])
                        setShowSuggestions(false)
                      }

                      return !prev
                    })
                  }}
                  className='h-10 w-10 p-0'
                  aria-label={scannerOpen ? 'Hide scanner' : 'Open scanner'}
                  title={scannerOpen ? 'Hide scanner' : 'Open scanner'}
                >
                  <Camera className='h-4 w-4' />
                </Button>
              </div>

              {searchLoading && (
                <span className='absolute bottom-0 left-0 h-0.5 w-full overflow-hidden rounded-full'>
                  <span className='animate-progress-bar absolute inset-y-0 w-1/2 rounded-full bg-primary/40' />
                </span>
              )}

              {showSuggestions && searchResults.length > 0 && (
                <ul className='absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover shadow-md'>
                  {searchResults.map((result) => (
                    <li key={`${result.isbn13}-${result.title}`}>
                      <button
                        type='button'
                        className='flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent'
                        onClick={() => handleSelectSuggestion(result)}
                      >
                        <div className='flex h-10 w-7 shrink-0 items-center justify-center overflow-hidden rounded-sm border bg-muted'>
                          {result.coverUrl ? (
                            <img src={result.coverUrl} alt='' className='h-full w-full object-cover' />
                          ) : (
                            <span className='text-[8px] text-muted-foreground'>?</span>
                          )}
                        </div>
                        <div className='min-w-0'>
                          <p className='truncate font-medium'>{result.title}</p>
                          <p className='truncate text-xs text-muted-foreground'>
                            {result.authors.length > 0 ? result.authors.join(', ') : 'Unknown author'}
                          </p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </form>

          {scannerOpen && <BarcodeScanner onDetected={handleScannerDetected} />}

          {lookupResult && (
            <BookReservationItem
              title={lookupResult.title}
              thumbnailUrl={lookupResult.coverUrl}
              author={lookupResult.authors[0] ?? 'Unknown author'}
              status={bookStatus}
              lastAction={bookStatusText}
            />
          )}

          {lookupResult && returnPrompt && (
            <div className='space-y-3'>
              <p className='text-sm'>{returnPrompt}</p>
              <div className='flex gap-2'>
                <Button type='button' onClick={handleReturnBook} disabled={returning} className='flex-1'>
                  {returning ? 'Saving...' : 'Yes, mark as returned'}
                </Button>
                <Button type='button' variant='outline' onClick={clearBook} disabled={returning} className='flex-1'>
                  No
                </Button>
              </div>
            </div>
          )}

          {lookupResult && !activeLoan && (
            <>
              <div className='space-y-2'>
                <div className='flex rounded-md border p-0.5'>
                  <button
                    type='button'
                    onClick={() => setLoanType('on-loan')}
                    className={`flex-1 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors ${
                      loanType === 'on-loan'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Lending to
                  </button>
                  <button
                    type='button'
                    onClick={() => setLoanType('borrowed')}
                    className={`flex-1 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors ${
                      loanType === 'borrowed'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Borrowing from
                  </button>
                </div>
                <p className='text-xs text-muted-foreground'>
                  {loanType === 'on-loan'
                    ? 'This is your book — you are loaning it to a friend.'
                    : "This is your friend's book — you are borrowing it from them."}
                </p>
              </div>

              <div className='space-y-2'>
                <Select value={friendId} onValueChange={setFriendId} disabled={!hasFriends}>
                  <SelectTrigger>
                    <SelectValue placeholder={hasFriends ? 'Select a friend' : 'Add friends first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {friends.map((friend) => (
                      <SelectItem key={friend.id} value={String(friend.id)}>
                        {friend.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className='flex items-center gap-2'>
                  <Input
                    value={quickFriendName}
                    onChange={(event) => setQuickFriendName(event.target.value)}
                    placeholder='Quick add friend name'
                    disabled={addingFriend || submitting}
                  />
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => void handleQuickAddFriend()}
                    disabled={addingFriend || submitting || !quickFriendName.trim()}
                    className='h-10 px-3'
                  >
                    {addingFriend ? 'Adding...' : 'Add'}
                  </Button>
                </div>
              </div>

              <Button type='button' onClick={handleConfirmBooking} disabled={submitting || !selectedFriend} className='w-full'>
                {submitting ? 'Saving...' : 'Confirm booking'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  )
}
